import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "@reach/router";

export type SegmentType = string;

export interface GlobalSettingsSegment {
  segment_id: string;
  segment_label: string;
  url_prefix: string;
}

export interface HidePrefixPath {
  path: string;
}

interface SegmentContextType {
  activeSegment: SegmentType;
  setActiveSegment: (segment: SegmentType) => void;
  segments: readonly GlobalSettingsSegment[];
  hidePrefixes: readonly HidePrefixPath[];
}

const SegmentContext = createContext<SegmentContextType>({
  activeSegment: "business", // default fallback
  setActiveSegment: () => {},
  segments: [],
  hidePrefixes: [],
});

export const useSegment = () => useContext(SegmentContext);

interface SegmentProviderProps {
  children: React.ReactNode;
  segments?: readonly GlobalSettingsSegment[];
  hidePrefixes?: readonly HidePrefixPath[];
}

export const SegmentProvider: React.FC<SegmentProviderProps> = ({ 
  children,
  segments = [],
  hidePrefixes = []
}) => {
  const { pathname } = useLocation();
  const getMatchedSegment = (currentPathname: string, currentSegments: readonly GlobalSettingsSegment[]) => {
    let matchedSegment = "business"; // fallback

    // Find the longest matching url prefix to ensure exact matching 
    // e.g. "/experts-comptables/" takes precedence over "/"
    const sortedSegments = [...currentSegments].sort((a, b) => 
      (b.url_prefix?.length || 0) - (a.url_prefix?.length || 0)
    );

    for (const seg of sortedSegments) {
      let prefix = seg.url_prefix;
      if (prefix && !prefix.startsWith("/")) prefix = `/${prefix}`;
      
      if (prefix && prefix !== "/" && currentPathname?.startsWith(prefix)) {
        matchedSegment = seg.segment_id;
        break;
      }
    }

    if (matchedSegment === "business") {
      // If no explicit long prefix matched, check if we match the base "/" prefix segment
      const baseSegment = currentSegments.find(s => s.url_prefix === "/");
      if (baseSegment) matchedSegment = baseSegment.segment_id;
    }

    return matchedSegment;
  };

  const [activeSegment, setActiveSegment] = useState<SegmentType>(() => getMatchedSegment(pathname, segments));

  useEffect(() => {
    setActiveSegment(getMatchedSegment(pathname, segments));
  }, [pathname, segments]);

  return (
    <SegmentContext.Provider value={{ activeSegment, setActiveSegment, segments, hidePrefixes }}>
      {children}
    </SegmentContext.Provider>
  );
};
